import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey, yellow } from "@mui/material/colors";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { useContext, useState } from "react";
import { TrackedCreatorStatus, TrackedCreatorWrapper } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";

const styles = {
  chip: {
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 3,
    px: 0.5,
  },
};

export default function CreatorCard({
  creatorWrapper,
  onClick,
  updateAutoTrack,
  onArchive,
}: {
  creatorWrapper: TrackedCreatorWrapper;
  onClick: (creatorWrapper: TrackedCreatorWrapper) => void;
  updateAutoTrack: (creatorId: number, newAutoTrackValue: boolean) => void;
  onArchive: (creatorId: number) => void;
}) {
  const creator = creatorWrapper.trackedCreator;
  const acc = creator.socialMediaAccount;
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { discoverTab } = useContext(DiscoverViewContext);

  const [imgError, setImgError] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const brandCount = creatorWrapper.sentCount + creatorWrapper.unsentCount;

  const statusLabel =
    creator.status === TrackedCreatorStatus.pending && brandCount === 0
      ? "Pending"
      : `${brandCount} brand${brandCount === 1 ? "" : "s"}`;

  return (
    <>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${grey[200]}`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          cursor: statusLabel === "Pending" ? "default" : "pointer",
        }}
        onClick={() => {
          if (statusLabel === "Pending") {
            return;
          }
          onClick(creatorWrapper);
        }}
      >
        <Grid container alignItems="center" gap={1} sx={{ p: 2 }}>
          <Grid
            item
            container
            xs={12}
            md={discoverTab ? 12 : 4}
            lg={3}
            gap={1}
            alignItems="center"
          >
            <Grid item xs="auto">
              {imgError || !acc.profilePicUrl ? (
                <Avatar
                  sx={{
                    width: 50,
                    height: 50,
                    fontSize: 14,
                    bgcolor: "#bdbdbd",
                  }}
                >
                  {acc.username?.[0]?.toUpperCase() || "?"}
                </Avatar>
              ) : (
                <Box
                  component="img"
                  src={acc.profilePicUrl}
                  alt={acc.username}
                  onError={() => setImgError(true)}
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              )}
            </Grid>
            <Grid item xs="auto">
              <Typography variant="h6" fontWeight={600}>
                {acc.username}
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={12} md="auto">
            <Tooltip
              enterTouchDelay={0}
              title={
                statusLabel === "Pending"
                  ? "Processing — may take a few hours"
                  : null
              }
            >
              <Chip
                size="small"
                sx={[
                  styles.chip,
                  {
                    backgroundColor:
                      statusLabel === "Pending" ? grey[100] : yellow[300],
                  },
                ]}
                label={statusLabel}
              />
            </Tooltip>
          </Grid>
          <Grid item xs />
          <Grid item xs="auto">
            <Tooltip
              enterTouchDelay={0}
              title="With autotracking on, we'll scan a creator's posts for new brands and keep their list automatically up to date"
            >
              <FormControlLabel
                onClick={(e) => e.stopPropagation()}
                control={
                  <Switch
                    size="small"
                    checked={creator.autoTrackingEnabled}
                    onChange={(e) => {
                      updateAutoTrack(creator.id, e.target.checked);
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontWeight: 500, fontSize: 13, ml: 1 }}>
                    Auto-tracking {creator.autoTrackingEnabled ? "ON" : "OFF"}
                  </Typography>
                }
              />
            </Tooltip>
          </Grid>
          <Grid item xs="auto">
            <Tooltip title="Remove tracked creator" enterTouchDelay={0}>
              <IconButton
                size="small"
                onClick={(e: CustomEvent) => {
                  e.stopPropagation();
                  setShowDeleteConfirm(true);
                }}
                sx={{ color: grey[500] }}
              >
                <Box component="span" className="fa-solid fa-trash" />
              </IconButton>
            </Tooltip>
          </Grid>
          <Tooltip
            title={
              statusLabel === "Pending"
                ? "Still Processing - this may take a few hours"
                : null
            }
            enterTouchDelay={0}
          >
            <Grid item xs="auto">
              <Button
                size="small"
                sx={{ textTransform: "none" }}
                variant="contained"
                disableElevation
                disabled={statusLabel === "Pending"}
                onClick={(e: CustomEvent) => {
                  e.stopPropagation();
                  onClick(creatorWrapper);
                }}
              >
                {!isMobileScreen ? "View Brands " : ""}
                <Box
                  component="span"
                  sx={{ ml: isMobileScreen ? 0 : 1 }}
                  className="fa-solid fa-arrow-right"
                />
              </Button>
            </Grid>
          </Tooltip>
        </Grid>
      </Card>

      <ConfirmDialog
        open={showDeleteConfirm}
        header="Remove Tracked Creator"
        subheader={`Are you sure you want to stop tracking @${acc.username}? You can always add them back later.`}
        handleClose={() => setShowDeleteConfirm(false)}
        handleConfirm={() => {
          setShowDeleteConfirm(false);
          onArchive(creator.id);
        }}
        confirmText="Remove"
        cancelText="Cancel"
      />
    </>
  );
}
